import { Project, Goal, Task, AuthUser } from '../types/models';
import { getToken, redirectToLogin } from '../auth';

const PRIMARY_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
const FALLBACK_URL = process.env.REACT_APP_FALLBACK_API_URL || '';

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const headers = {
    ...(init?.headers as Record<string, string> | undefined),
    ...authHeaders(),
  };
  const options = { ...init, headers };

  try {
    const res = await fetch(`${PRIMARY_URL}${path}`, options);
    if (res.status === 401) {
      redirectToLogin();
      throw new Error('Unauthorized');
    }
    if (res.ok || !FALLBACK_URL) return res;
    // Primary returned an error status — try fallback
    const fallbackRes = await fetch(`${FALLBACK_URL}${path}`, options);
    if (fallbackRes.status === 401) {
      redirectToLogin();
      throw new Error('Unauthorized');
    }
    return fallbackRes;
  } catch (err: any) {
    if (err?.message === 'Unauthorized') throw err;
    // Primary unreachable — try fallback
    if (!FALLBACK_URL) throw new Error('API unavailable');
    const fallbackRes = await fetch(`${FALLBACK_URL}${path}`, options);
    if (fallbackRes.status === 401) {
      redirectToLogin();
      throw new Error('Unauthorized');
    }
    return fallbackRes;
  }
}

export const api = {
  // Generic request method for pipeline manager and other features
  async request(path: string, init?: RequestInit): Promise<any> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(init?.headers as Record<string, string> | undefined),
    };
    const response = await apiFetch(path, { ...init, headers });
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    if (response.status === 204) return null;
    return response.json();
  },

  async getMe(): Promise<AuthUser> {
    const response = await apiFetch('/me');
    if (!response.ok) throw new Error('Failed to fetch user');
    return response.json();
  },

  async getProjects(): Promise<Project[]> {
    const response = await apiFetch('/projects');
    if (!response.ok) throw new Error('Failed to fetch projects');
    return response.json();
  },

  async getProject(id: string): Promise<Project> {
    const response = await apiFetch(`/projects/${id}`);
    if (!response.ok) throw new Error('Failed to fetch project');
    return response.json();
  },

  async createProject(project: Omit<Project, 'id' | 'createdAt' | 'updatedAt' | 'goals'>): Promise<Project> {
    const response = await apiFetch('/projects', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(project),
    });
    if (!response.ok) throw new Error('Failed to create project');
    return response.json();
  },

  async updateProject(id: string, project: Project): Promise<Project> {
    const response = await apiFetch(`/projects/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(project),
    });
    if (!response.ok) throw new Error('Failed to update project');
    return response.json();
  },

  async deleteProject(id: string): Promise<void> {
    const response = await apiFetch(`/projects/${id}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Failed to delete project');
  },

  async createGoal(projectId: string, goal: Omit<Goal, 'id' | 'tasks'>): Promise<Goal> {
    const response = await apiFetch(`/projects/${projectId}/goals`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(goal),
    });
    if (!response.ok) throw new Error('Failed to create goal');
    return response.json();
  },

  async createTask(projectId: string, goalId: string, task: Omit<Task, 'id'>): Promise<Task> {
    const response = await apiFetch(`/projects/${projectId}/goals/${goalId}/tasks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(task),
    });
    if (!response.ok) throw new Error('Failed to create task');
    return response.json();
  },
};
